import { Box, Button, Stack, SvgIcon, Typography, SxProps, useTheme } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import throttle from 'lodash/throttle';
import { useEffect, useRef, useState, ReactNode } from 'react';
import { TNavbarPage } from '../../types/admin';

export type PillsNavbarItem = {
  key: string;
  src: string;
  title: string;
  description: string;
  show3DBadge?: boolean;
};

export type PillsNavbarProps = {
  items: PillsNavbarItem[];
  onItemClick?: (key: string) => void;
  textSx?: SxProps;
  containerSx?: SxProps;
  badge3DIcon?: ReactNode;
  isAdminPreview?: boolean;
};

export function PillsNavbar({
  items,
  onItemClick,
  textSx = {},
  containerSx = {},
  badge3DIcon,
  isAdminPreview = false,
}: PillsNavbarProps) {
  const { direction, shadows } = useTheme();
  const [isOverflow, setIsOverflow] = useState(false);
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 600 : false;

  const flexContainerRef = useRef<HTMLDivElement>(null);

  const [emblaRef] = useEmblaCarousel({
    dragFree: true,
    active: isOverflow,
    direction,
  });

  // we want the carousel active only when there are more items than the container width
  useEffect(() => {
    const handleResize = throttle(() => {
      const clientWidth = flexContainerRef.current?.clientWidth || 0;
      const scrollWidth = flexContainerRef.current?.scrollWidth || 0;

      setIsOverflow(clientWidth < scrollWidth);
    }, 500);

    window.addEventListener('resize', handleResize);

    // we need to wait for dom to be ready
    setTimeout(() => {
      handleResize();
    }, 1000);

    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleClick = (page: TNavbarPage) => {
    const id = isAdminPreview ? page.previewSectionsKey || page.key : page.key;
    if (onItemClick) {
      onItemClick(id);
    }
    const targetElement = document.querySelector(`#section-${id}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        ...containerSx,
      }}
      ref={emblaRef}
    >
      <Stack ref={flexContainerRef} direction="row" spacing={2} sx={{ pb: '2px' }}>
        {[...items].reverse().map((item, index) => (
          <Button
            key={index}
            onClick={() => handleClick(item)}
            sx={{
              display: 'flex',
              width: 'auto',
              minWidth: 'fit-content',
              justifyContent: 'flex-start',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center',
              padding: '10px',
            }}
            data-treedistestid={`nav-pills-item-${item.key.toLowerCase()}`}
          >
            {item.src && (
              <img
                src={item.src}
                width={isMobile ? 60 : 70}
                height={isMobile ? 60 : 70}
                alt="section image"
                style={{
                  objectFit: 'cover',
                  borderRadius: '50%',
                  background: '#EDEDED',
                  padding: '4px',
                }}
              />
            )}
            <Stack alignItems="flex-start" spacing={{ xs: '2px' }} overflow="hidden">
              <Typography
                lineHeight="20px"
                fontSize={{ xs: '14px', sm: '16px' }}
                color="grey.900"
                sx={{
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  ...textSx,
                }}
              >
                {item.title}
                {item.show3DBadge && badge3DIcon && (
                  <SvgIcon
                    sx={{
                      width: 35,
                      mb: -0.9,
                      mx: 0.5,
                      mt: -2,
                    }}
                  >
                    {badge3DIcon}
                  </SvgIcon>
                )}
              </Typography>

              {/* {!isMobile && (
                <Typography
                  fontSize={{ xs: '12px', sm: '13px' }}
                  lineHeight={{ xs: 'normal', sm: '18px' }}
                  sx={{
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    ...textSx,
                  }}
                >
                  {item.description}
                </Typography>
              )} */}
            </Stack>
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
